import serializeNode from "dom-serializer";
import { isTag } from "domhandler";
import HtmlToReact, { Parser } from "html-to-react";
import { find } from "lodash";
import defaultsDeep from "lodash/defaultsDeep";
import React, { useContext, useEffect } from "react";
import styled, { css } from "styled-components";

import { ContentStyles, SelectInput } from "@examspark/common-ui";
import Math from "@examspark/common-ui/dist/components/Math";
import { scoreSingleGap } from "@examspark/score";
import { StemTextInline, Stem } from "@examspark/types";

import Mark from "../common/Mark";
import QuestionContext from "../QuestionContext";

import Prompt from "./shared/Prompt";
import EquationEntry from "./tools/EquationEntry";

import "./InlineText.css";

interface ClozeProps {
  fontSize?: string;
  interactionType?: "gap" | "graphic-gap" | "editor";
  key?: number;
  mark: false | "correct" | "incorrect";
  mathKeyboardPreset?: Stem.MathKeyboardPreset;
  inputWidth?: number;
  onChange: (value: string) => void;
  options: any[];
  textAlign?: string;
  value?: string;
}
// This also _has_ to be defined somewhere else.
interface InlineTextProps {
  data: StemTextInline;
}

type StyledGapProps = {
  isGraphicGap: boolean;
  fontSize?: string;
  textAlign?: string;
};

const StyledInlineChoice = styled.select<StyledGapProps>`
  ${({ isGraphicGap }) =>
    isGraphicGap &&
    css`
      width: 100%;
      height: 100%;
    `}

  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `};

  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `};
`;

export const RichInlineChoice = (props: ClozeProps) => {
  return (
    <SelectInput
      isClearable
      useRichTextOptions
      value={props.value}
      onChange={(e) => {
        props.onChange(e?.value ?? "");
      }}
      options={
        props.options.map((opt) => ({
          value: opt.id,
          label: opt.body,
        })) as { value: string; label: string }[]
      }
      styles={{
        container: (base: any) => ({
          ...base,
          display: "inline-block",
          verticalAlign: "middle",
          minHeight: "30px",
        }),
        // allow the menu to be as wide as the longest option
        menu: (base: any) => ({
          ...base,
          width: "max-content",
        }),
        // disable the default height for on our common
        // select input in order to allow tall images/math/etc.
        valueContainer: (base: any) => ({
          ...base,
          padding: "0px 8px",
        }),
        control: (base: any) => ({
          ...base,
          minHeight: "30px",
        }),
      }}
    />
  );
};

export const InlineChoice = (props: ClozeProps) => {
  return (
    <span className="item-player-inline-choice-gap">
      {props.mark && <Mark type={props.mark} />}
      <StyledInlineChoice
        isGraphicGap={props.interactionType === "graphic-gap"}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        fontSize={props.fontSize}
        textAlign={props.textAlign}
      >
        <option value=""></option>
        {props.options.map((opt) => {
          return (
            <option key={opt.id} value={opt.id}>
              {opt.body}
            </option>
          );
        })}
      </StyledInlineChoice>
    </span>
  );
};

// This is a hack to make the gap expand to fit the content.
// We copy the input text to an invisible span and use grid
// to have the span and input exactly overlap, then grow this
// container to the size of the span (and thus the input).
const StyledTextEntryGap = styled.span<{ inputWidth?: string }>`
  display: inline-grid;

  ${({ inputWidth }) => inputWidth && `width: min(100%, ${inputWidth});`};

  &:after {
    box-sizing: border-box;
    content: attr(data-value) " ";
    visibility: hidden;
    white-space: pre-line;
    word-wrap: anywhere;

    // this padding was found experientally. it causes
    // the text to be in the same place as the input
    // which is necessary for textarea to expand veritcally
    padding: 5px 4px 1.4lh;
    line-height: 1.5rem;
    ${({ inputWidth }) => inputWidth && `width: min(100%, ${inputWidth});`};

    grid-area: 1/1;
  }
`;

const StyledTextEntry = styled.input<StyledGapProps>`
  box-sizing: border-box;

  grid-area: 1/1;

  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `};

  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `};

  ${({ isGraphicGap }) =>
    isGraphicGap &&
    css`
      width: 100%;
      height: 100%;
    `};
`;
export const TextEntry = (props: ClozeProps) => {
  return (
    <StyledTextEntryGap
      className="item-player-text-entry-gap"
      data-value={props.mark ? props.value : undefined}
    >
      {props.mark && <Mark type={props.mark} />}
      <StyledTextEntry
        isGraphicGap={props.interactionType === "graphic-gap"}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        fontSize={props.fontSize}
        textAlign={props.textAlign}
      />
    </StyledTextEntryGap>
  );
};
const StyledExtendedEntry = styled.textarea<StyledGapProps>`
  box-sizing: border-box;
  resize: none;
  grid-area: 1/1;

  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `};

  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `};

  ${({ isGraphicGap }) =>
    isGraphicGap &&
    css`
      width: 100%;
      height: 100%;
    `};
`;
export const ExtendedEntry = (props: ClozeProps) => {
  return (
    <StyledTextEntryGap
      className="item-player-text-entry-gap"
      data-value={props.value}
      inputWidth={`${props.inputWidth ? `${props.inputWidth}px` : "100%"}`}
    >
      {/* note: no mark, because we cannot autoscore */}
      <StyledExtendedEntry
        // this is the 'default' size of the textarea
        // before our styles and stuff, setting it low
        // so that we have a reasonably small "minimum"
        // width
        cols={5}
        maxLength={1024}
        isGraphicGap={props.interactionType === "graphic-gap"}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        fontSize={props.fontSize}
        textAlign={props.textAlign}
      />
    </StyledTextEntryGap>
  );
};

export const MathEntry = (props: ClozeProps) => {
  const additionalStyles: React.CSSProperties = {};

  // ensure the mark is able to position itself correctly
  if (props.interactionType === "graphic-gap") {
    additionalStyles["display"] = "block";
  }

  return (
    <span className="item-player-math-entry-gap" style={additionalStyles}>
      {props.mark && <Mark type={props.mark} />}
      <EquationEntry interactionType="gap" {...props} />
    </span>
  );
};

const InlineText = (props: InlineTextProps) => {
  const [state, dispatch] = useContext(QuestionContext);

  // Reveal Key
  useEffect(() => {
    if (state.revealKey) {
      const keys: any = {};
      for (const gapId in props.data.scoring?.gaps) {
        const gap = props.data.scoring?.gaps[gapId];
        const gapContent = props.data.content.gaps[gapId];
        let keyFound = false;

        if (gap?.options) {
          for (const optionId in gap.options) {
            if (!keyFound) {
              if (gap.options[optionId].isKey) {
                keyFound = true;
                console.log("scoring gap", gapContent);
                switch (gapContent.type) {
                  case "inline-choice":
                  case "inline-choice-richtext":
                    keys[gapId] = optionId;
                    break;
                  case "text-entry":
                  case "math-entry":
                  case "numeric-entry":
                  case "extended-entry":
                    // we need the text of the option
                    keys[gapId] = find(props.data.content.gaps[gapId].options, [
                      "id",
                      optionId,
                    ])?.body;
                    break;
                }
              }
            }
          }
        }
      }

      dispatch({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            gaps: keys,
          },
        },
      });
    }
  }, [state.revealKey]);

  const getGapById = (gapId: string) => {
    return props.data.content.gaps[gapId];
  };

  const getText = () => {
    return props.data.content.text?.body || "";
  };

  const setResponseForGap = (gapId: string, value: string) => {
    const rsp = defaultsDeep(
      {
        [gapId]: value,
      },
      state?.response?.[props.data.id]?.gaps,
    );

    return rsp;
  };

  const getResponseForGap = (gapId: string) => {
    return state?.response?.[props.data.id]?.gaps?.[gapId];
  };

  const handleChange = (gapId: string, value: string) => {
    const rsp = setResponseForGap(gapId, value);

    dispatch({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: {
          gaps: rsp,
        },
      },
    });
  };

  const htmlToReactParser = new Parser();
  const processNodeDefinitions = HtmlToReact.ProcessNodeDefinitions();
  const reactElement = htmlToReactParser.parseWithInstructions(
    getText(),
    () => true,
    [
      {
        shouldProcessNode: (node) =>
          isTag(node) && node.attribs?.["data-gap-id"] != null,
        processNode: function (node, children, index) {
          if (!isTag(node)) return null;
          const gapId: string = node.attribs?.["data-gap-id"] ?? "";
          const gap = getGapById(gapId);
          const rsp = getResponseForGap(gapId);

          // The element type to render
          let ReactElementType: (props: ClozeProps) => JSX.Element = TextEntry;

          if (gap.type === "inline-choice") {
            ReactElementType = InlineChoice;
          } else if (gap.type === "inline-choice-richtext") {
            ReactElementType = RichInlineChoice;
          } else if (gap.type === "math-entry") {
            ReactElementType = MathEntry;
          } else if (gap.type === "extended-entry") {
            ReactElementType = ExtendedEntry;
          }

          const mark = scoreSingleGap(
            gap,
            gapId,
            state.response?.[props.data.id]?.gaps,
            props.data,
            props.data.scoring?.gaps,
          )
            ? "correct"
            : "incorrect";

          const gapProps: ClozeProps = {
            key: index,
            value: rsp ? rsp : "",
            options: gap.options,
            mathKeyboardPreset: gap.mathKeyboardPreset,
            inputWidth: gap.inputWidth,
            mark: state.scoreResponses ? mark : false,
            onChange: (value: string) => {
              handleChange(gapId, value);
            },
          };

          return React.createElement(ReactElementType, gapProps);
        },
      },
      {
        // pre-process MathML
        shouldProcessNode: (node) => isTag(node) && node?.name === "math",
        processNode: function (node) {
          return <Math display={false} mathml={serializeNode(node)} />;
        },
      },
      {
        // Anything else
        shouldProcessNode: () => true,
        processNode: processNodeDefinitions.processDefaultNode,
      },
    ],
  );

  return (
    <div className="" data-interaction-type={props.data.interactionType}>
      <Prompt data={props.data.content.prompt} />
      <div className="item-player-element-body item-player-inline-text">
        <ContentStyles>{reactElement}</ContentStyles>
      </div>
    </div>
  );
};
export default InlineText;
